import React, { useState, useRef } from 'react';
import { Button } from './ui/Button';
import { Phone, ArrowRight, ShieldCheck, Sparkles, CheckCircle2, Clock, CreditCard } from 'lucide-react';
interface SmsGateProps {
  onUnlock: () => void;
  barColor: string;
}
export function SmsGate({
  onUnlock,
  barColor
}: SmsGateProps) {
  const [step, setStep] = useState<'phone' | 'code' | 'verifying'>('phone');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [generatedCode, setGeneratedCode] = useState('');
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const formatPhone = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 10);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPhone(formatPhone(e.target.value));
    setError('');
  };
  const handleSendCode = (e: React.FormEvent) => {
    e.preventDefault();
    const digits = phone.replace(/\D/g, '');
    if (digits.length < 10) {
      setError('Please enter a valid 10-digit phone number');
      return;
    }
    const fakeCode = String(Math.floor(100000 + Math.random() * 900000));
    setGeneratedCode(fakeCode);
    setStep('code');
    setError('');
    setTimeout(() => inputRefs.current[0]?.focus(), 100);
  };
  const handleCodeChange = (index: number, value: string) => {
    if (value.length > 1) {
      const digits = value.replace(/\D/g, '').slice(0, 6);
      const newCode = [...code];
      digits.split('').forEach((d, i) => {
        if (index + i < 6) newCode[index + i] = d;
      });
      setCode(newCode);
      const nextIndex = Math.min(index + digits.length, 5);
      inputRefs.current[nextIndex]?.focus();
      return;
    }
    const digit = value.replace(/\D/g, '');
    const newCode = [...code];
    newCode[index] = digit;
    setCode(newCode);
    setError('');
    if (digit && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };
  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };
  const handleVerify = (e: React.FormEvent) => {
    e.preventDefault();
    const entered = code.join('');
    if (entered.length < 6) {
      setError('Please enter the full 6-digit code');
      return;
    }
    setStep('verifying');
    setTimeout(() => {
      onUnlock();
    }, 1200);
  };
  return <div className="relative" data-id="element-300">
      <div className="absolute -top-24 left-0 right-0 h-24 bg-gradient-to-b from-transparent to-[#0B0F14] pointer-events-none z-10" data-id="element-301" />

      <div className="relative z-20 bg-[#111318] rounded-md shadow-2xl border border-[#2a2f3b] p-8 md:p-10 max-w-lg mx-auto" data-id="element-302">
        {step === 'phone' && <>
            <div className="text-center mb-6" data-id="element-303">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 bg-[#E10600]/10 border border-[#E10600]/30 shadow-[0_0_20px_rgba(225,6,0,0.15)]" data-id="element-304">
                <Sparkles className="w-8 h-8 text-[#E10600]" data-id="element-305" />
              </div>
              <h3 className="text-3xl font-display font-bold text-white mb-2 tracking-wide" data-id="element-306">
                START YOUR FREE TRIAL
              </h3>
              <p className="text-[#9ca3af] text-sm max-w-sm mx-auto mb-4" data-id="element-307">
                Get instant access to your full results and AI-powered coaching
                platform.
              </p>

              {/* Value bullets */}
              <div className="text-left space-y-3 mb-6 bg-[#0B0F14] rounded-md p-5 border border-[#2a2f3b]" data-id="element-308">
                <div className="flex items-center gap-3" data-id="element-309">
                  <CheckCircle2 className="w-4 h-4 text-[#E10600] flex-shrink-0" data-id="element-310" />
                  <span className="text-[#F5F5F4] text-sm" data-id="element-311">
                    Complete superpower breakdown & trait analysis
                  </span>
                </div>
                <div className="flex items-center gap-3" data-id="element-312">
                  <CheckCircle2 className="w-4 h-4 text-[#E10600] flex-shrink-0" data-id="element-313" />
                  <span className="text-[#F5F5F4] text-sm" data-id="element-314">
                    AI sales coach personalized to your profile
                  </span>
                </div>
                <div className="flex items-center gap-3" data-id="element-315">
                  <CheckCircle2 className="w-4 h-4 text-[#E10600] flex-shrink-0" data-id="element-316" />
                  <span className="text-[#F5F5F4] text-sm" data-id="element-317">
                    Custom development plan & weekly action items
                  </span>
                </div>
                <div className="flex items-center gap-3" data-id="element-318">
                  <CheckCircle2 className="w-4 h-4 text-[#E10600] flex-shrink-0" data-id="element-319" />
                  <span className="text-[#F5F5F4] text-sm" data-id="element-320">
                    Role-fit recommendations & coaching guidance
                  </span>
                </div>
              </div>

              {/* Trust signals */}
              <div className="flex items-center justify-center gap-6 mb-2" data-id="element-321">
                <div className="flex items-center gap-1.5 text-[#6B7280] text-xs uppercase tracking-widest" data-id="element-322">
                  <Clock className="w-3.5 h-3.5" data-id="element-323" />
                  <span data-id="element-324">7 Days Free</span>
                </div>
                <div className="flex items-center gap-1.5 text-[#6B7280] text-xs uppercase tracking-widest" data-id="element-325">
                  <CreditCard className="w-3.5 h-3.5" data-id="element-326" />
                  <span data-id="element-327">No CC Needed</span>
                </div>
              </div>
            </div>

            <form onSubmit={handleSendCode} data-id="element-328">
              <div className="mb-5" data-id="element-329">
                <label className="block text-xs font-bold text-[#6B7280] uppercase tracking-widest mb-2" data-id="element-330">
                  Phone Number
                </label>
                <div className="relative" data-id="element-331">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none" data-id="element-332">
                    <Phone className="h-4 w-4 text-[#6B7280]" data-id="element-333" />
                  </div>
                  <input type="tel" value={phone} onChange={handlePhoneChange} placeholder="(555) 123-4567" className="block w-full pl-10 pr-3 py-4 border border-[#2a2f3b] rounded-md bg-[#0B0F14] text-white placeholder-[#4b5563] focus:outline-none focus:bg-[#1a1f2b] focus:ring-1 focus:ring-[#E10600] focus:border-[#E10600] transition text-lg tracking-wide" autoFocus data-id="element-334" />
                </div>
                {error && <p className="text-[#E10600] text-xs mt-2 font-medium" data-id="element-335">
                    {error}
                  </p>}
              </div>

              <Button type="submit" fullWidth size="lg" className="group" data-id="element-336">
                START MY FREE TRIAL
                <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" data-id="element-337" />
              </Button>

              <p className="text-[10px] text-[#4b5563] text-center mt-4 leading-relaxed" data-id="element-338">
                By continuing, you agree to receive a verification code via SMS.
                Your trial begins immediately — cancel anytime. No credit card
                required.
              </p>
            </form>
          </>}

        {(step === 'code' || step === 'verifying') && <>
            <div className="text-center mb-8" data-id="element-339">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 bg-[#E10600]/10" data-id="element-340">
                <ShieldCheck className="w-8 h-8 text-[#E10600]" data-id="element-341" />
              </div>
              <h3 className="text-3xl font-display font-bold text-white mb-2 tracking-wide" data-id="element-342">
                VERIFY YOUR NUMBER
              </h3>
              <p className="text-[#9ca3af] text-sm" data-id="element-343">
                We sent a 6-digit code to{' '}
                <span className="font-medium text-white" data-id="element-344">{phone}</span>
              </p>
            </div>

            <form onSubmit={handleVerify} data-id="element-345">
              <div className="flex justify-center gap-2 mb-8" data-id="element-346">
                {code.map((digit, idx) => <input key={idx} ref={el => {
              inputRefs.current[idx] = el;
            }} type="text" inputMode="numeric" maxLength={6} value={digit} onChange={e => handleCodeChange(idx, e.target.value)} onKeyDown={e => handleKeyDown(idx, e)} className="w-12 h-16 text-center text-2xl font-bold font-display border border-[#2a2f3b] rounded-md bg-[#0B0F14] text-white focus:outline-none focus:bg-[#1a1f2b] focus:ring-1 focus:ring-[#E10600] focus:border-[#E10600] transition" disabled={step === 'verifying'} data-id="element-347" />)}
              </div>

              {error && <p className="text-[#E10600] text-xs text-center mb-4 font-medium" data-id="element-348">
                  {error}
                </p>}

              <Button type="submit" fullWidth size="lg" disabled={step === 'verifying'} data-id="element-349">
                {step === 'verifying' ? <span className="flex items-center" data-id="element-350">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" data-id="element-351">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-352" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" data-id="element-353" />
                    </svg>
                    ACTIVATING YOUR TRIAL...
                  </span> : 'ACTIVATE MY FREE TRIAL'}
              </Button>

              <button type="button" onClick={() => {
            setStep('phone');
            setCode(['', '', '', '', '', '']);
            setError('');
          }} className="block mx-auto mt-6 text-xs text-[#6B7280] hover:text-white transition-colors uppercase tracking-widest font-bold" data-id="element-354">
                Use a different number
              </button>
            </form>
          </>}
      </div>
    </div>;
}